import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { apiFetch } from '../../../utils';

import { setUser } from "../../../store/session";

import ModalButton from "../../modals/modal_button";
import LoginModal from "../../modals/login";
import SignUpModal from "../../modals/signup";
import CreateCharacterModal from "../../modals/create_character";
import RulesModal from "../../modals/rules";
import HighscoresModal from "../../modals/highscores";
import BountiesModal from "../../modals/bounties";
import CharacterSearchModal from "../../modals/character_search";

import './index.css';

const NavBarComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [showCommunityDropdown, setShowCommunityDropdown] = useState(false);
    const [showShopDropdown, setShowShopDropdown] = useState(false);
    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [width, setWidth] = useState(() => window.innerWidth);
    const currentUser = useSelector(state => state.session.user);
    const communityCloseTimerRef = useRef(null);
    const shopCloseTimerRef = useRef(null);
    const accountCloseTimerRef = useRef(null);

    const clearTimer = (timerRef) => {
        if (!timerRef.current) return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
    };

    const openCommunityDropdown = () => {
        clearTimer(communityCloseTimerRef);
        setShowCommunityDropdown(true);
        setShowShopDropdown(false);
        setShowAccountDropdown(false);
    };

    const closeCommunityDropdownWithDelay = () => {
        clearTimer(communityCloseTimerRef);
        communityCloseTimerRef.current = setTimeout(() => {
            setShowCommunityDropdown(false);
        }, 120);
    };

    const openShopDropdown = () => {
        clearTimer(shopCloseTimerRef);
        setShowShopDropdown(true);
        setShowCommunityDropdown(false);
        setShowAccountDropdown(false);
    };

    const closeShopDropdownWithDelay = () => {
        clearTimer(shopCloseTimerRef);
        shopCloseTimerRef.current = setTimeout(() => {
            setShowShopDropdown(false);
        }, 120);
    };

    const openAccountDropdown = () => {
        clearTimer(accountCloseTimerRef);
        setShowAccountDropdown(true);
        setShowCommunityDropdown(false);
        setShowShopDropdown(false);
    };

    const closeAccountDropdownWithDelay = () => {
        clearTimer(accountCloseTimerRef);
        accountCloseTimerRef.current = setTimeout(() => {
            setShowAccountDropdown(false);
        }, 120);
    };

    const handleLogout = async () => {
        setLoading(true);
        const response = await apiFetch('/user/logout', 'POST');
        if (response.code !== 'success') {
            setLoading(false);
            return alert('The server failed to log you out! You are still logged in.');
        }
        dispatch(setUser(null));
        setLoading(false);
    };

    useEffect(() => {
        const updateWidth = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener('resize', updateWidth);
        updateWidth();

        return () => {
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    useEffect(() => {
        return () => {
            clearTimer(communityCloseTimerRef);
            clearTimer(shopCloseTimerRef);
            clearTimer(accountCloseTimerRef);
        };
    }, []);

    const getActivePage = (name) => {
        const page = location.pathname;
        if (page.includes(name)) {
            return 'navbar-button-active'
        } else if (page === '/' && name === 'home') {
            return 'navbar-button-active'
        };
        return '';
    };

    const getMainButtonClass = (name) => `button navbar-button ${getActivePage(name)}`;
    const useIcons = width <= 760;

    const getButtonLabel = (name) => {
        if (!useIcons) return name;

        const icons = {
            Ameria: <i className="fa-solid fa-house" />,
            Community: <i className="fa-solid fa-users" />,
            Download: <i className="fa-solid fa-download" />,
            Wiki: <i className="fa-brands fa-wikipedia-w" />,
            Shop: <i className="fa-solid fa-shop" />,
            Account: <i className="fa-regular fa-circle-user" />,
        };

        return icons[name] ?? name;
    };

    return (
        <div id='navbar-container'
            onMouseLeave={() => {
                clearTimer(communityCloseTimerRef);
                clearTimer(shopCloseTimerRef);
                clearTimer(accountCloseTimerRef);
                setShowCommunityDropdown(false);
                setShowShopDropdown(false);
                setShowAccountDropdown(false);
            }}
        >
            <div id='navbar-flex-container'>
                <div id='navbar-navigation-buttons'
                    onMouseEnter={() => {
                        clearTimer(accountCloseTimerRef);
                        setShowAccountDropdown(false);
                    }}
                >
                    <div id='navbar-page-buttons'>
  
                        <div className='navbar-button-aligner' onClick={() => navigate('/')}>
                            <button className={getMainButtonClass('home')} title='Home'/>
                            <p className='navbar-button-label'>{getButtonLabel('Ameria')}</p>
                        </div>



                        <div id='navbar-community-container' className='navbar-button-aligner'
                            onMouseEnter={openCommunityDropdown}
                            onMouseLeave={closeCommunityDropdownWithDelay}
                        >
                            <button className={`${getMainButtonClass('community')} navbar-community-button`} title='Community'/>
                            <p className='navbar-button-label'>{getButtonLabel('Community')}</p>

                            <div
                                className={showCommunityDropdown ? 'navbar-community-dropdown navbar-community-dropdown-loaded' : 'navbar-community-dropdown'}
                                onMouseEnter={() => clearTimer(communityCloseTimerRef)}
                                onMouseLeave={closeCommunityDropdownWithDelay}
                            >
                                <ModalButton
                                    className='button navbar-account-button'
                                    type='button'
                                    title='Characters'
                                    buttonText='Characters'
                                    modalComponent={<CharacterSearchModal />}
                                    onButtonClick={() => setShowCommunityDropdown(false)}
                                />
                                <button className='button navbar-account-button' type='button' title='Guilds'
                                    onClick={() => navigate('/community/guilds')}
                                >
                                    Guilds
                                </button>
                                <ModalButton
                                    className='button navbar-account-button'
                                    type='button'
                                    title='Highscores'
                                    buttonText='Highscores'
                                    modalComponent={<HighscoresModal />}
                                    onButtonClick={() => setShowCommunityDropdown(false)}
                                />
                                <ModalButton
                                    className='button navbar-account-button'
                                    type='button'
                                    title='Bounty'
                                    buttonText='Bounty'
                                    modalComponent={<BountiesModal />}
                                    onButtonClick={() => setShowCommunityDropdown(false)}
                                />
                                <button className='button navbar-account-button' type='button' title='Polls'
                                    onClick={() => navigate('/community/polls')}
                                >
                                    Polls
                                </button>
                                <ModalButton
                                    className='button navbar-account-button'
                                    type='button'
                                    title='Rules'
                                    buttonText='Rules'
                                    modalComponent={<RulesModal />}
                                    onButtonClick={() => setShowCommunityDropdown(false)}
                                />
                            </div>
                        </div>


                        <a className='link' href='https://ameriax.org/client_download/Ameria1.0.0.zip' target='_blank' rel="noreferrer">
                            <div className='navbar-button-aligner'>
                                <button className='button navbar-button' title='Download Client'/>
                                <p className='navbar-button-label'>{getButtonLabel('Download')}</p>
                            </div>
                        </a>


                        <div className='navbar-button-aligner'>
                            <button id='navbar-wiki-button' className={`${getMainButtonClass('wiki')}`} title='Wiki'/>
                            <p className='navbar-button-label'>{getButtonLabel('Wiki')}</p>
                        </div>


                        <div id='navbar-shop-container' className='navbar-button-aligner'
                            onMouseEnter={openShopDropdown}
                            onMouseLeave={closeShopDropdownWithDelay}
                        >
                            <button id='navbar-shop-button' className={`${getMainButtonClass('shop')}`} title='Shop'/>
                            <p className='navbar-button-label'>{getButtonLabel('Shop')}</p>

                            <div
                                className={showShopDropdown ? 'navbar-shop-dropdown navbar-shop-dropdown-loaded' : 'navbar-shop-dropdown'}
                                onMouseEnter={() => clearTimer(shopCloseTimerRef)}
                                onMouseLeave={closeShopDropdownWithDelay}
                            >
                                <button className='button navbar-account-button' type='button' title='Donate'
                                    onClick={() => navigate('/shop/donate')}
                                >
                                    Donate
                                </button>

                                <button className='button navbar-account-button' type='button' title='Market'
                                    onClick={() => navigate('/shop/market')}
                                >
                                    Market
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <div id='navbar-account-buttons-container'>

                    {currentUser === null ? <div id='navbar-account-container'>

                        <div className='navbar-button-aligner'>
                            <button className='button navbar-button navbar-button-active navbar-acc-btn' title='Account'
                                onMouseEnter={openAccountDropdown}
                            />
                            <p className='navbar-button-label'>{getButtonLabel('Account')}</p>
                        </div>


                        <div className={showAccountDropdown ? "navbar-account-dropdown navbar-account-dropdown-loaded" : "navbar-account-dropdown"}
                            onMouseEnter={() => clearTimer(accountCloseTimerRef)}
                            onMouseLeave={closeAccountDropdownWithDelay}
                        >
                            <ModalButton
                                title='Login'
                                className='button navbar-account-button'
                                modalComponent={<LoginModal />}
                                buttonText='Login'
                            />
                            <ModalButton
                                title='Signup'
                                className='button navbar-account-button'
                                modalComponent={<SignUpModal />}
                                buttonText='Signup'
                            />
                        </div>
                    </div>
                    :
                    <div id='navbar-account-container'>

                        <div className='navbar-button-aligner'>
                            <button className='button navbar-button navbar-button-active navbar-acc-btn' title='Account'
                                onMouseEnter={openAccountDropdown}
                            />
                            <p className='navbar-button-label'>{getButtonLabel('Account')}</p>
                        </div>
 
                        <div className={showAccountDropdown ? "navbar-account-dropdown navbar-account-dropdown-loaded" : "navbar-account-dropdown"}
                            onMouseEnter={() => clearTimer(accountCloseTimerRef)}
                            onMouseLeave={closeAccountDropdownWithDelay}
                        >
                            {currentUser.is_admin &&
                                <div className='navbar-button-aligner'>
                                    <button
                                        className='button navbar-account-button'
                                        type='button'
                                        title='Admin Panel'
                                        disabled={loading}
                                        onClick={() => navigate('/admin')}
                                    />
                                    <p id='admin-panel-p' onClick={() => navigate('/admin')}>{loading ? 'Loading' : 'Admin Panel'}</p>
                                </div>
                            }

                            <div className='navbar-button-aligner'>
                                <button className='button navbar-account-button' type='button' title='Manage'
                                    disabled={loading}
                                    onClick={() => navigate('/account')}
                                />
                                <p id='manage-p' onClick={() => navigate('/account')}>{loading ? 'Loading' : 'Manage'}</p>
                            </div>
                            
                            <div className='navbar-button-aligner'>
                                <ModalButton
                                    title='Create Character'
                                    className='button navbar-account-button'
                                    modalComponent={<CreateCharacterModal />}
                                />
                                <p id='create-character-p'>Create Character</p>
                            </div>

                            <div className='navbar-button-aligner'>
                                <button className='button navbar-account-button' type='button' title='Logout'
                                    disabled={loading}
                                    onClick={handleLogout}
                                />
                                <p id='logout-p' onClick={handleLogout}>{loading ? 'Loading' : 'Logout'}</p>
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default NavBarComponent;